import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import {
  addSingleRoleMenuListSA,
  deleteMultipleRoleSA,
  editSingleRoleMenuListSA,
  getSearchListRolesNameSA,
  getSingleRoleMenuListSA,
} from "../../api/MasterRole";
import { IMsRole } from "../../interfaces/IMsRole";
import { RoleFormData } from "../../interfaces/RoleFormData";
import { selectNewProfile } from "../../store/newProfile/selector";

interface Return {
  data: {
    loadingActive: boolean;
    listRolesName: IMsRole[];
    roleDetail: RoleFormData;
  };
  method: {
    onGetListRolesName(search?: string): void;
    onGetRoleDetail(name?: string, id?: string): void;
    onChangeFormValue(formData: RoleFormData): void;
    onCreateNewRole(formData: RoleFormData): void;
    onEditRole(
      selectedData: { name: string; id: string },
      formData: RoleFormData
    ): void;
    onDeleteRole(roleList: string[]): void;
  };
}

export default function useViewModel(): Return {
  const [loadingActive, setLoadingActive] = useState(true);
  const [listRolesName, setListRolesName] = useState<IMsRole[]>([]);
  const [roleDetail, setRoleDetail] = useState<RoleFormData>({
    name: "",
    accesses: [],
  });
  const profile = useSelector(selectNewProfile);

  const onGetListRolesName = useCallback(
    async (search: string) => {
      setLoadingActive(true);
      await getSearchListRolesNameSA(search)
        .then((res) => setListRolesName(res))
        .then(() => {
          setLoadingActive(false);
        })
        .catch((error) => {
          console.log("Error fetching data:", error);
          setLoadingActive(false);
        });
    },
    [getSearchListRolesNameSA]
  );

  const onGetRoleDetail = useCallback(
    async (name?: string, id?: string) => {
      setLoadingActive(true);
      if (!id || !name) {
        setRoleDetail({
          name: "",
          accesses: [
            { menuName: "Home", menuValue: false },
            { menuName: "Master Role", menuValue: false },
            { menuName: "Master User", menuValue: false },
            { menuName: "List of Request", menuValue: false },
            { menuName: "CRR Report", menuValue: false },
            { menuName: "Questionnaire Report", menuValue: false },
          ],
        });
        setLoadingActive(false);
        return;
      }
      await getSingleRoleMenuListSA(id)
        .then((res) => setRoleDetail({ name: name!, accesses: res }))
        .then(() => setLoadingActive(false))
        .catch((error) => {
          console.log("Error fetching data:", error);
          setLoadingActive(false);
        });
    },
    [getSingleRoleMenuListSA]
  );

  const onChangeFormValue = useCallback((formData: RoleFormData) => {
    setRoleDetail(formData);
  }, []);

  const onCreateNewRole = useCallback(async (payload: RoleFormData) => {
    setLoadingActive(true);
    await addSingleRoleMenuListSA(
      payload.accesses,
      payload.name,
      profile.BinusianId
    )
      .then(() => {
        setLoadingActive(false);
        onGetListRolesName("");
      })
      .catch((error) => {
        console.log("Error fetching data:", error);
        setLoadingActive(false);
      });
  }, []);

  const onEditRole = useCallback(
    async (
      selectedData: { name: string; id: string },
      formData: RoleFormData
    ) => {
      setLoadingActive(true);
      await editSingleRoleMenuListSA(
        formData.accesses,
        selectedData.name,
        formData.name,
        selectedData.id,
        profile.BinusianId
      )
        .then(() => {
          setLoadingActive(false);
          onGetListRolesName("");
        })
        .catch((error) => {
          console.log("Error fetching data:", error);
          setLoadingActive(false);
        });
    },
    [editSingleRoleMenuListSA]
  );

  const onDeleteRole = useCallback(
    async (roleList: string[]) => {
      setLoadingActive(true);
      await deleteMultipleRoleSA(roleList, profile.BinusianId)
        .then(() => {
          setLoadingActive(false);
          onGetListRolesName("");
        })
        .catch((error) => {
          console.log("Error fetching data:", error);
          setLoadingActive(false);
        });
    },
    [deleteMultipleRoleSA]
  );

  return {
    data: {
      loadingActive,
      listRolesName,
      roleDetail,
    },
    method: {
      onGetListRolesName,
      onGetRoleDetail,
      onChangeFormValue,
      onCreateNewRole,
      onEditRole,
      onDeleteRole,
    },
  };
}
